import { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { statusConfig, priorityConfig } from "@/mocks/ticketData";
import type { TicketAttachment } from "@/mocks/ticketData";
import type { ZammadUserTicket } from "@/mocks/zammadTickets";
import { fetchTicketDetail, submitLiveTicketRating, closeLiveTicket, addLiveTicketComment } from "@/services/zammadService";
import AttachmentPicker from "@/components/feature/AttachmentPicker";
import TicketAttachments from "./TicketAttachments";

interface TicketDetailModalProps {
  ticket: ZammadUserTicket;
  isOpen: boolean;
  onClose: () => void;
  onTicketUpdated?: (ticket: ZammadUserTicket) => void;
  customerIdentity?: string;
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return "N/A";
  const ticketDate = new Date(dateStr);
  if (isNaN(ticketDate.getTime())) return "N/A";

  const day = ticketDate.getDate();
  const month = ticketDate.getMonth() + 1;
  const year = ticketDate.getFullYear();
  const hours = String(ticketDate.getHours()).padStart(2, "0");
  const minutes = String(ticketDate.getMinutes()).padStart(2, "0");
  const months = [
    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
  ];
  return `${day} ${months[month - 1]} ${year}, ${hours}:${minutes}`;
}

const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;

function TicketBody({ content }: { content: string }) {
  const containsHtml = HTML_TAG_PATTERN.test(content);
  const sanitizedHtml = useMemo(
    () => (containsHtml ? DOMPurify.sanitize(content, { USE_PROFILES: { html: true } }) : ""),
    [containsHtml, content]
  );

  if (!containsHtml) {
    return (
      <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line break-words">
        {content}
      </p>
    );
  }

  return (
    <div
      className="ticket-html-content text-sm text-foreground-700 leading-relaxed break-words"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}

export default function TicketDetailModal({
  ticket,
  isOpen,
  onClose,
  onTicketUpdated,
  customerIdentity,
}: TicketDetailModalProps) {
  const [detailedTicket, setDetailedTicket] = useState<ZammadUserTicket | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);

  const [commentText, setCommentText] = useState("");
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Usar la versión detallada si existe, de lo contrario la versión resumida
  const activeTicket = detailedTicket || ticket;
  const activeSatisfactionRating = activeTicket.satisfactionRating;
  const ticketAttachments = useMemo(() => {
    const uniqueAttachments = new Map<string, TicketAttachment>();
    for (const event of activeTicket.timeline || []) {
      for (const attachment of event.attachments || []) {
        uniqueAttachments.set(`${attachment.articleId}-${attachment.id}`, attachment);
      }
    }
    return Array.from(uniqueAttachments.values());
  }, [activeTicket.timeline]);

  const handleCloseTicket = async () => {
    if (!activeTicket.zammadId || !customerIdentity) return;
    setClosingTicket(true);
    try {
      const ok = await closeLiveTicket(activeTicket.zammadId, customerIdentity);
      if (ok) {
        // Al cerrar el ticket, se mapea al estado "closed" en el frontend
        const updated: ZammadUserTicket = {
          ...activeTicket,
          status: "closed" as const,
        };
        setDetailedTicket(updated);
        if (onTicketUpdated) {
          onTicketUpdated(updated);
        }
      } else {
        alert("No se pudo cerrar el ticket. Intenta de nuevo.");
      }
    } catch (err) {
      console.error("Error closing ticket:", err);
    } finally {
      setClosingTicket(false);
    }
  };

  const handleSendComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      (!commentText.trim() && commentFiles.length === 0)
      || !activeTicket.zammadId
      || !customerIdentity
    ) return;
    setSubmittingComment(true);
    try {
      const ok = await addLiveTicketComment(
        activeTicket.zammadId,
        commentText.trim() || "Archivo adjunto.",
        customerIdentity,
        commentFiles
      );
      if (ok) {
        setCommentText("");
        setCommentFiles([]);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        alert("No se pudo enviar el comentario. Intenta de nuevo.");
      }
    } catch (err) {
      console.error("Error sending comment:", err);
    } finally {
      setSubmittingComment(false);
    }
  };

  useEffect(() => {
    if (activeSatisfactionRating !== null && activeSatisfactionRating !== undefined) {
      setSelectedRating(activeSatisfactionRating);
    } else {
      setSelectedRating(0);
    }
    setHoveredRating(0);
    setRatingSubmitted(false);
  }, [isOpen, activeSatisfactionRating]);

  const ticketId = ticket.id;

  useEffect(() => {
    if (!isOpen) {
      setDetailedTicket(null);
      return;
    }

    let active = true;
    async function loadDetails() {
      setLoadingDetails(true);
      setError(null);
      try {
        const data = await fetchTicketDetail(ticketId, customerIdentity);
        if (active) {
          setDetailedTicket(data);
          if (onTicketUpdated) {
            onTicketUpdated(data);
          }
        }
      } catch (err) {
        console.error("Error loading ticket details:", err);
        if (active) {
          setError("No se pudieron cargar los detalles e historial del ticket.");
        }
      } finally {
        if (active) {
          setLoadingDetails(false);
        }
      }
    }

    loadDetails();

    return () => {
      active = false;
    };
  }, [isOpen, ticketId, refreshTrigger, customerIdentity, onTicketUpdated]);

  const handleRateTicket = async (score: number) => {
    if (!activeTicket.zammadId || !customerIdentity) return;
    setSubmittingRating(true);
    try {
      const ok = await submitLiveTicketRating(activeTicket.zammadId, score, customerIdentity);
      if (ok) {
        setRatingSubmitted(true);
        setSelectedRating(score);
        
        const updated: ZammadUserTicket = {
          ...activeTicket,
          satisfactionRating: score
        };
        
        setDetailedTicket(updated);
        
        if (onTicketUpdated) {
          onTicketUpdated(updated);
        }
      } else {
        alert("No se pudo guardar la calificación. Intenta de nuevo.");
      }
    } catch (err) {
      console.error("Error submitting rating:", err);
    } finally {
      setSubmittingRating(false);
    }
  };

  if (!isOpen) return null;

  const s = statusConfig[activeTicket.status];
  const p = priorityConfig[activeTicket.priority];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative w-full max-w-2xl bg-background-50 rounded-2xl overflow-hidden animate-[fadeIn_0.2s_ease-out] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-background-200/70">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 mb-2 flex-wrap">
                <span className="text-xs font-mono text-foreground-500 bg-background-100 px-2.5 py-1 rounded-md">
                  {activeTicket.id}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.color} ${s.border} border`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.color.replace("text-", "bg-")}`}></span>
                  {s.label}
                </span>
                <span className={`text-xs font-semibold ${p.color}`}>
                  Prioridad {p.label}
                </span>
                {activeTicket.slaBreached && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                    <i className="ri-alert-line text-[10px]"></i>
                    SLA excedido
                  </span>
                )}
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground-950">
                {activeTicket.title}
              </h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-price-tag-3-line text-[11px]"></i>
                  {activeTicket.category}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-[11px]"></i>
                  {activeTicket.assignedTo || "Mesa de Ayuda"}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-[11px]"></i>
                  {formatDateTime(activeTicket.createdAt)}
                </span>
              </div>
              {activeTicket.satisfactionRating !== null && activeTicket.satisfactionRating !== undefined && (
                <div className="flex items-center gap-1 mt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${i < activeTicket.satisfactionRating! ? "ri-star-fill text-amber-500" : "ri-star-line text-foreground-300"}`}
                    ></i>
                  ))}
                  <span className="text-xs text-foreground-500 ml-1">{activeTicket.satisfactionRating}/5</span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors flex-shrink-0"
            >
              <i className="ri-close-line text-foreground-600"></i>
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {loadingDetails ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-8 h-8 border-3 border-accent-500/20 border-t-accent-500 rounded-full animate-spin mb-3"></div>
              <p className="text-xs text-foreground-500">Cargando datos...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <i className="ri-error-warning-line text-amber-500 text-3xl mb-2"></i>
              <p className="text-sm font-medium text-foreground-750">{error}</p>
              <p className="text-xs text-foreground-500 mt-1">El ticket existe pero no pudimos conectar con los servicios de historial.</p>
            </div>
          ) : (
            <>
              {/* Description */}
              <div className="px-6 py-4 border-b border-background-200/70">
                <h4 className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                  Descripción del Ticket
                </h4>
                <TicketBody content={activeTicket.description || "Sin descripción adicional."} />
              </div>

              <TicketAttachments
                attachments={ticketAttachments}
                customerIdentity={customerIdentity}
              />

              {/* Calificar Servicio (Solo para tickets cerrados) */}
              {activeTicket.status === "closed" && (
                <div className="px-6 py-4 border-b border-background-200/70 bg-amber-50/20">
                  <h4 className="text-[11px] font-semibold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <i className="ri-feedback-line"></i>
                    {activeTicket.satisfactionRating !== null && activeTicket.satisfactionRating !== undefined
                      ? "Calificación de la atención"
                      : "Califica la atención recibida"}
                  </h4>
                  {ratingSubmitted ? (
                    <div className="flex items-center gap-2 text-emerald-600 text-xs font-semibold py-1">
                      <i className="ri-checkbox-circle-fill text-lg"></i>
                      <span>¡Gracias! Tu valoración ha sido guardada.</span>
                    </div>
                  ) : activeTicket.satisfactionRating !== null && activeTicket.satisfactionRating !== undefined ? (
                    <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold py-1">
                      <i className="ri-star-fill text-base"></i>
                      <span>Ya calificaste este ticket con {activeTicket.satisfactionRating}/5.</span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs text-foreground-600">
                        Tu ticket ha concluido. Por favor, califica la calidad del soporte que recibiste:
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onMouseEnter={() => setHoveredRating(star)}
                              onMouseLeave={() => setHoveredRating(0)}
                              onClick={() => handleRateTicket(star)}
                              disabled={submittingRating}
                              className="text-2xl cursor-pointer transition-transform hover:scale-115 focus:outline-none"
                              title={`Calificar con ${star} estrellas`}
                            >
                              <i
                                className={`text-2xl ${
                                  star <= (hoveredRating || selectedRating)
                                    ? "ri-star-fill text-amber-500"
                                    : "ri-star-line text-foreground-300"
                                }`}
                              ></i>
                            </button>
                          ))}
                        </div>
                        {submittingRating && (
                          <div className="w-4 h-4 border-2 border-amber-500/20 border-t-amber-500 rounded-full animate-spin"></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Agregar Comentario */}
              <div className="px-6 py-4 border-b border-background-200/70 bg-background-50/50">
                  <h4 className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <i className="ri-chat-new-line"></i>
                    Agregar Comentario
                  </h4>
                  <form onSubmit={handleSendComment} className="flex flex-col gap-2.5">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Escribe tu mensaje o actualización para la Mesa de Servicios..."
                      maxLength={1000}
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-450 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all resize-none"
                    ></textarea>
                    <AttachmentPicker
                      files={commentFiles}
                      onChange={setCommentFiles}
                      disabled={submittingComment}
                    />
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={
                          submittingComment
                          || (!commentText.trim() && commentFiles.length === 0)
                        }
                        className="px-5 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submittingComment ? (
                          <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        ) : (
                          <i className="ri-send-plane-2-line text-xs"></i>
                        )}
                        {submittingComment ? "Enviando..." : "Enviar Comentario"}
                      </button>
                    </div>
                  </form>
              </div>

              {/* Timeline */}
              <div className="px-6 py-4">
                <h4 className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                  Historial de Atención
                </h4>
                <div className="relative">
                  {activeTicket.timeline && activeTicket.timeline.length > 0 ? (
                    <>
                      <div className="absolute left-[17px] top-2 bottom-2 w-px bg-background-200"></div>
                      <div className="space-y-0">
                        {activeTicket.timeline.map((event, idx) => (
                          <div key={idx} className="relative flex gap-3.5 pb-4 last:pb-0">
                            <div
                              className={`relative z-10 w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0 ${
                                idx === 0
                                  ? "bg-primary-500"
                                  : "bg-background-100 border-2 border-background-200"
                              }`}
                            >
                              <i
                                className={`${event.icon} text-sm ${
                                  idx === 0 ? "text-white" : "text-foreground-400"
                                }`}
                              ></i>
                            </div>
                            <div className="flex-1 min-w-0 pt-1">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 mb-0.5">
                                <h5 className="text-[13px] font-semibold text-foreground-900">{event.title}</h5>
                                <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                                  {formatDateTime(event.date)}
                                </span>
                              </div>
                              <p className="text-xs text-foreground-600 leading-relaxed whitespace-pre-line">{event.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-foreground-500">No hay historial disponible para este ticket.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-3 border-t border-background-200/70 flex items-center justify-between">
          <span className="text-[10px] text-foreground-400">
            Sincronizado: {formatDateTime(activeTicket.lastSyncedAt)}
          </span>
          <div className="flex items-center gap-3">
            {activeTicket.status !== "closed" && (
              <button
                type="button"
                onClick={handleCloseTicket}
                disabled={closingTicket}
                className="px-4 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {closingTicket ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <i className="ri-checkbox-circle-line"></i>
                )}
                {closingTicket ? "Cerrando..." : "Cerrar ticket"}
              </button>
            )}
            <span className="text-[10px] text-foreground-400 flex items-center gap-1">
              <i className="ri-refresh-line"></i>
              ID interno: {activeTicket.zammadId}
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ticket-html-content p,
        .ticket-html-content ul,
        .ticket-html-content ol,
        .ticket-html-content blockquote,
        .ticket-html-content table {
          margin: 0 0 0.75rem;
        }
        .ticket-html-content p:last-child,
        .ticket-html-content ul:last-child,
        .ticket-html-content ol:last-child,
        .ticket-html-content blockquote:last-child,
        .ticket-html-content table:last-child {
          margin-bottom: 0;
        }
        .ticket-html-content ul,
        .ticket-html-content ol {
          padding-left: 1.5rem;
        }
        .ticket-html-content ul { list-style: disc; }
        .ticket-html-content ol { list-style: decimal; }
        .ticket-html-content a {
          color: var(--color-primary-600, #2563eb);
          text-decoration: underline;
        }
        .ticket-html-content img {
          max-width: 100%;
          height: auto;
        }
        .ticket-html-content table {
          display: block;
          max-width: 100%;
          overflow-x: auto;
          border-collapse: collapse;
        }
        .ticket-html-content th,
        .ticket-html-content td {
          padding: 0.375rem 0.5rem;
          border: 1px solid rgb(226 232 240);
        }
      `}</style>
    </div>
  );
}
